import React, { useCallback, useEffect, useState } from "react";
import { Check, CheckCircle2, Loader2, MessageSquare, Reply, Send } from "lucide-react";
import { toast } from "sonner";
import { crud, dosyaNotApi } from "../lib/api";
import { useAuth } from "../lib/auth";

function fmt(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("tr-TR");
    } catch {
        return iso;
    }
}

export default function DosyaYorumlariSection({ dosyaId, onChanged }) {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [metin, setMetin] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [replyTo, setReplyTo] = useState(null);

    const load = useCallback(async () => {
        if (!dosyaId) return;
        setLoading(true);
        try {
            const data = await crud.list("dosya-yorumlari", { sigorta_dosyasi: dosyaId });
            setItems(Array.isArray(data) ? data : data.results || []);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [dosyaId]);

    useEffect(() => {
        load();
    }, [load]);

    async function kaydet(e) {
        e.preventDefault();
        if (!metin.trim()) return;
        setSaving(true);
        try {
            if (replyTo) {
                await dosyaNotApi.cevapla(replyTo.id, metin.trim());
            } else {
                await crud.create("dosya-yorumlari", {
                    sigorta_dosyasi: dosyaId,
                    metin: metin.trim(),
                    kullanici: user?.id,
                    hatirlat: true,
                });
            }
            setMetin("");
            setReplyTo(null);
            toast.success("Not eklendi.");
            await load();
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Kaydedilemedi.");
        } finally {
            setSaving(false);
        }
    }

    return (
        <section className="rounded-xl border border-[#D4AF37]/25 bg-[#121A2F]/80 p-4 mb-6">
            <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-[#D4AF37]" />
                    <h3 className="font-display text-lg text-white">Dosya Notları</h3>
                </div>
                <span className="text-[10px] text-slate-500">
                    Silinmez · okundu/halledildi · günlük hatırlatma
                </span>
            </div>
            {loading ? (
                <div className="flex items-center gap-2 text-sm text-slate-400 py-4">
                    <Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…
                </div>
            ) : items.length === 0 ? (
                <p className="text-sm text-slate-500 mb-3">Henüz operasyon notu yok.</p>
            ) : (
                <ul className="space-y-2 mb-4 max-h-80 overflow-y-auto">
                    {items.map((y) => (
                        <li
                            key={y.id}
                            className={`rounded-lg border px-3 py-2 text-sm ${
                                y.cozuldu
                                    ? "border-emerald-500/25 bg-emerald-500/5"
                                    : !y.okundu
                                      ? "border-sky-500/30 bg-sky-500/5"
                                      : "border-white/10 bg-[#0B0F19]/60"
                            }`}
                        >
                            <div className="flex items-start justify-between gap-2 mb-1">
                                <div className="text-[10px] text-slate-500">
                                    {fmt(y.olusturma_tarihi)} · {y.kullanici_label || "—"}
                                </div>
                                <div className="flex gap-1">
                                    {!y.okundu ? (
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                await dosyaNotApi.okundu(y.id);
                                                load();
                                                onChanged?.();
                                            }}
                                            className="p-1 text-sky-300 hover:bg-white/5 rounded"
                                            title="Okundu"
                                        >
                                            <Check className="h-3.5 w-3.5" />
                                        </button>
                                    ) : null}
                                    {!y.cozuldu ? (
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                await dosyaNotApi.cozuldu(y.id, true);
                                                toast.success("Halledildi");
                                                load();
                                                onChanged?.();
                                            }}
                                            className="p-1 text-emerald-300 hover:bg-white/5 rounded"
                                            title="Halledildi"
                                        >
                                            <CheckCircle2 className="h-3.5 w-3.5" />
                                        </button>
                                    ) : null}
                                    <button
                                        type="button"
                                        onClick={() => setReplyTo(y)}
                                        className="p-1 text-slate-400 hover:bg-white/5 rounded"
                                        title="Alıntıyla cevapla"
                                    >
                                        <Reply className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                            <p className="text-slate-200 whitespace-pre-wrap text-xs">{y.metin}</p>
                        </li>
                    ))}
                </ul>
            )}
            {replyTo ? (
                <div className="text-[10px] text-[#D4AF37] mb-2">
                    Alıntılı cevap #{replyTo.id}{" "}
                    <button type="button" className="underline ml-1" onClick={() => setReplyTo(null)}>
                        iptal
                    </button>
                </div>
            ) : null}
            <form onSubmit={kaydet} className="flex gap-2">
                <input
                    value={metin}
                    onChange={(e) => setMetin(e.target.value)}
                    placeholder="Operasyon notu ekle…"
                    className="flex-1 bg-[#0B0F19] border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:border-[#D4AF37] outline-none"
                />
                <button
                    type="submit"
                    disabled={saving || !metin.trim()}
                    className="h-10 px-4 rounded-md bg-[#D4AF37] text-slate-900 text-xs font-semibold disabled:opacity-40 flex items-center gap-1"
                >
                    {saving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <Send className="h-4 w-4" />
                    )}
                    Kaydet
                </button>
            </form>
        </section>
    );
}
